import React, { createContext, PureComponent } from 'react';
import {
  BzitManufacturerLongShortListMap,
  BzitSearchParameters,
  BzitVehicleSearch,
  BzitWheelPosition,
  SearchParams,
  ServiceResult
} from '../../bzit';
import { AdditionalSearchParams } from '../../configs';
import { userCountry } from '../../configs/env';
import { searchForm } from '../../configs/searchForm';
import { reportError } from '../../error-handling/reportError';
import { withErrorBoundary } from '../../error-handling/withErrorBoundary';
import * as SearchService from '../../helper/fetch/SearchService';
import offlineStore from '../../store/offlineStore';
import { Maybe } from '../../types';
import { withLang, WithLangProps } from '../LanguageProvider/withLang';
import { withTrackingApi, WithTrackingApiProps } from '../TrackingProvider/withTrackingApi';
import { withTypo3, WithTypo3Props } from '../Typo3Provider/withTypo3';
import { getParameters, searchVehicles } from './fetcher';
import { Sortings } from './sorting';
import { SearchContextType, SearchProviderProps, SearchState } from './types';
import {
  countryCodeCorrections,
  emptyState,
  extractResult,
  getOffersByLocation,
  getOffersByManufacturer,
  getSortedCountriesWithLocations,
  getSortedLocationCodes,
  getVehicleList,
  languageCodeCorrections,
  locationsFromSearchData,
  mkAdditionalSearchParams,
  mkSearchParams
} from './utils';

export const SearchContext: SearchContextType = createContext({
  ...emptyState()
});

export { mkAdditionalSearchParams, mkSearchParams };

class SearchProvider extends PureComponent<
  SearchProviderProps & WithTypo3Props & WithLangProps & WithTrackingApiProps,
  SearchState
> {
  // #endregion

  public state: SearchState = {
    ...emptyState(),
    locale: this.getLocale(),
    setCountryId: (countryId: string) => this.setCountryId(countryId),
    setSearchFormData: (formParams: SearchParams) =>
      this.setSearchFormData(formParams),
    setAdditionalSearchParams: (
      additionalSearchParams: AdditionalSearchParams
    ) => this.setAdditionalSearchParams(additionalSearchParams),
    getVehicleList: this.getVehicleList.bind(this),
    getOffersByLocation: this.getOffersByLocation.bind(this),
    getOffersByCountry: this.getOffersByCountry.bind(this),
    getOffersByManufacturer: this.getOffersByManufacturer.bind(this),
    setSorting: (sorting: Sortings) => this.setSorting(sorting),
    setDetailNavigationOffers: (offers: BzitVehicleSearch[]) =>
      this.setDetailNavigationOffers(offers),
    resetDetailNavigationOffers: () => this.resetDetailNavigationOffers()
  };
  // #region getter
  public getLanguageCode() {
    const { languageCode } = this.props.lang;
    const correction = languageCodeCorrections[languageCode];
    if (correction) {
      return correction;
    }
    return languageCode;
  }

  public getCountryCode() {
    const { countryCode } = this.props.lang;
    const correction = countryCodeCorrections[countryCode];
    if (correction) {
      return correction;
    }
    return countryCode;
  }

  public getLocale() {
    return `${this.getLanguageCode()}-${this.getCountryCode()}`;
  }

  async getVehicleList(formParams: SearchParams, vehicleIds: string[]) {
    return this.props.typo3.requireVehicleSlugs(await getVehicleList(formParams, vehicleIds));
  }

  async getOffersByLocation(formParams: SearchParams, codeLocation: string) {
    return this.props.typo3.requireVehicleSlugs(await getOffersByLocation(formParams, codeLocation));
  }

  async getOffersByManufacturer(formParams: SearchParams, nameManufacturer: string) {
    return this.props.typo3.requireVehicleSlugs(await getOffersByManufacturer(formParams, nameManufacturer));
  }

  /**
   * @param {string} countryCode e.g. de, en, fr
   */
  async getOffersByCountry(
    formParams: SearchParams,
    countryCode: string
  ): Promise<BzitVehicleSearch[]> {
    const searchParameterResult: ServiceResult<
      BzitSearchParameters
    > = await getParameters({});
    if (searchParameterResult.success) {
      const searchParameters = extractResult(searchParameterResult.result);
      const codeCountryPrefix = `CN-${countryCode.toUpperCase()}`;
      const codeCountry = Object.keys(searchParameters.locations).find(
        code => code.indexOf(codeCountryPrefix) === 0
      );
      if (codeCountry) {
        const { nameCurrency, codeWheelpos } = formParams;
        const results = await searchVehicles(
          mkSearchParams({
            ...{ nameCurrency, codeWheelpos },
            codeCountry
            // 20180801 - show all vehicles, not only offers
            // onlyOfferVehicles: true
          })
        );
        if (results.success) {
          return this.props.typo3.requireVehicleSlugs(results.result.vehicles);
        }

        throw results.error;
      }
    }
    console.warn(
      `SearchProvider.getOffersByCountry: couldn't find any countryCode for '${countryCode}'`
    );
    return [];
  };
  // #endregion

  // #region countryId
  public queryCountryId = async (): Promise<string> => {
    const countryId: Maybe<string> = await offlineStore.tryGetItem(
      'SearchProvider.countryId'
    );
    return countryId || '';
  };

  public putCountryId = async (countryId: string): Promise<void> => {
    await offlineStore.trySetItem('SearchProvider.countryId', countryId);
  };
  // #endregion

  // #region formData
  public queryFormData = async (): Promise<Maybe<SearchParams>> => {
    return offlineStore.tryGetItem('SearchProvider.searchParams');
  };

  public putFormData = async (formData: SearchParams): Promise<void> => {
    await offlineStore.trySetItem('SearchProvider.searchParams', formData);
  };

  public getCountryAndLanguageData = () => {
    // setting wheel position
    const countriesForRighthandsideDrive = [
      'GB', // Great Bitain and Nothern Ireland
      'IE', // Ireland
      'MT', // Malta
      'CY' // Cyprus
    ];

    const languagesForRighthandsideDrive = [
      'ga', // Ireland
      'mt', // Malta
      'en-GB'  //GB only as language instead of country
    ];

    const countriesCurrency: { [country: string]: string } = {
      GB: 'GBP',
      CH: 'CHF',
      DK: 'DKK',
      CZ: 'CZK',
      SE: 'SEK',
      PL: 'PLN',
      RO: 'RON',
      US: 'USD'
    };

    const languageCurrency: { [language: string]: string } = {
      cy: 'GBP',
      ga: 'EUR',
      gd: 'GBP',
      mt: 'EUR',
      uk: 'GBP',
      en: 'EUR'
    };

    return {
      codeWheelpos:
        countriesForRighthandsideDrive.some(
          countryCode => userCountry === countryCode
        ) ||
        languagesForRighthandsideDrive.some(
          languageCode => navigator.language === languageCode
        )
          ? ('R' as BzitWheelPosition)
          : ('L' as BzitWheelPosition),
      nameCurrency:
        userCountry in countriesCurrency
          ? countriesCurrency[userCountry]
          : navigator.language in languageCurrency
          ? languageCurrency[navigator.language]
          : 'EUR'
    };
  };

  public ensureFormData = async (): Promise<SearchParams> => {
    const formData = await this.queryFormData();
    if (!formData) {
      const newFormData = {
        ...searchForm,
        ...this.getCountryAndLanguageData()
      };
      await this.putFormData(newFormData);
      return newFormData;
    }
    return {
      ...formData,
      ...this.getCountryAndLanguageData()
    };
  };
  // #endregion

  // #region setter
  public setSearchFormData = (formParams: SearchParams) => {
    this.setState({
      formParams,
      resultsAreDirty: true,
    });
  };

  public setAdditionalSearchParams = (
    additionalSearchParams: AdditionalSearchParams
  ) => {
    this.setState({ additionalSearchParams });
  };

  public setSorting = (sorting: Sortings) => {
    this.setState({
      sorting
    });
  };

  public setDetailNavigationOffers = (offers: BzitVehicleSearch[]) => {
    this.setState({
      detailNavigationOffers: offers
    });
  };

  public resetDetailNavigationOffers = () => {
    this.setState({
      detailNavigationOffers: []
    });
  };

  public setCountryId = (countryId: string) => {
    this.setState({
      countryId
    });
  };

  public getAllVehicles = async () => {
    const allVehicles = await SearchService.search(mkSearchParams({}));
    if (allVehicles.success) {
      const { result } = allVehicles;

      const mappedVehicles = await this.props.typo3.requireVehicleSlugs(result.vehicles);

      const vehicles = mappedVehicles.map((vehicle: BzitVehicleSearch) => {
        return vehicle;
      });

      this.setState({
        allVehicles: vehicles
      });
    }
  };

  public fetchContentAsync = async () => {
    this.setState({ paramsLoaded: false });
    try {
      const searchParameterResult = await getParameters({
        // deactivated because this affects the list of available locations
        // countryId
      })
      await this.props.typo3.staticContentReady;
      if (searchParameterResult.success) {
        const searchParameters = extractResult(searchParameterResult.result);
        const {
          lastUpdate,
          locations,
          nameCurrency,
          nameEuronorm
        } = searchParameters;
        const nameManufacturer = Object.assign(
          {},
          ...searchParameters.nameManufacturer
        );
        const nameManufacturerLong = Object.assign(
          {},
          ...searchParameters.nameManufacturerLong
        );
        const nameManufacturerShort = Object.assign(
          {},
          ...searchParameters.nameManufacturerShort
        );

        const manufacturerMaps: BzitManufacturerLongShortListMap[] = [nameManufacturerLong, nameManufacturerShort];
        manufacturerMaps.forEach((map: BzitManufacturerLongShortListMap) => {
          // Temporary fix: BZIT sometimes sends manufacturers that have the same model multiple times. So we strip these duplicates here #57004
          Object.values(map).forEach((manufacturer) => {
            if (!manufacturer) {
              return;
            }

            manufacturer.models = Array.from(new Set(manufacturer.models));
          });
        });

        const languageCode = this.getLanguageCode();

        const locationsGroupedByCountries = getSortedCountriesWithLocations(
          searchParameters,
          this.props.typo3.translateUsedVehicles,
          languageCode
        );
        const countryIdByLocationId: Map<string, string> = new Map();
        locationsGroupedByCountries.forEach((group) => {
          group.locationCodes.forEach((locationCode) => {
            countryIdByLocationId.set(locationCode, group.countryCode);
          });
        });

        this.setState({
          paramsLoaded: true,
          searchParameters: {
            locations,
            lastUpdate,
            nameCurrency,
            nameEuronorm,
            nameManufacturer,
            nameManufacturerLong,
            nameManufacturerShort
          },
          locations: locationsFromSearchData(searchParameters),
          locationsGroupedByCountries,
          countryIdByLocationId,
          sortedLocationCodes: getSortedLocationCodes(
            searchParameters.locations,
            languageCode
          )
        });
      } else {
        if (!searchParameterResult.success) {
          throw searchParameterResult.error;
        }
      }
    } catch (error) {
      reportError('SearchProvider.fetchContentAsync()', error);
      this.setState({ paramsError: error });
    } finally {
      this.setState({ paramsLoaded: true });
    }
  };

  private updateResults = async (formParams: SearchParams, additionalSearchParams: AdditionalSearchParams) => {
    this.putFormData(formParams);

    this.props.typo3.updateVehicleSlugs(); // Trigger updating of bus mappings for lower latency

    const response = await searchVehicles(formParams);
    if (this.state.formParams !== formParams) {
      // Form params have changed while the search has been running. So don't save this results.
      return;
    }

    if (response.success) {
      const { result } = response;

      const mappedVehicles = await this.props.typo3.requireVehicleSlugs(result.vehicles);
      const filteredSimilarVehicles = await this.props.typo3.requireVehicleSlugs(result.vehiclesSimilar || []);

      this.setState({
        apiSearchResult: mappedVehicles,
        searchResult: this.filterResultsWithAdditionalParams(mappedVehicles, additionalSearchParams),
        alternativeOffers: filteredSimilarVehicles,
        resultsLoaded: true,
        resultsAreDirty: false,
      });
    } else {
      reportError('SearchProvider.updateResults()', response.error);
    }
  };

  public getCountUsagesByCode = (
    vehicles: BzitVehicleSearch[],
    type: string,
    code: string
  ) => {
    return vehicles.filter(vehicle =>
      type === 'bodies' ? vehicle.codeBody === code : vehicle.codeUsage === code
    ).length;
  };

  public getCountUsages = (allVehicles: BzitVehicleSearch[]) => {
    // filter by codeWheelposition
    const { codeWheelpos } = this.state.formParams;
    const vehicles =
      codeWheelpos !== ''
        ? allVehicles.filter(vehicle => vehicle.codeWheelpos === codeWheelpos)
        : allVehicles;
    return {
      usages: {
        E01: this.getCountUsagesByCode(vehicles, 'usages', 'E01'),
        E02: this.getCountUsagesByCode(vehicles, 'usages', 'E02'),
        E03: this.getCountUsagesByCode(vehicles, 'usages', 'E03'),
        E09: this.getCountUsagesByCode(vehicles, 'usages', 'E09')
      },
      bodies: {
        A01: this.getCountUsagesByCode(vehicles, 'bodies', 'A01'),
        A02: this.getCountUsagesByCode(vehicles, 'bodies', 'A02'),
        A03: this.getCountUsagesByCode(vehicles, 'bodies', 'A03')
      }
    };
  };

  private filterResultsWithAdditionalParams = (
    searchResult: BzitVehicleSearch[],
    additionalSearchParams: AdditionalSearchParams
  ): BzitVehicleSearch[] => {
    if (!additionalSearchParams.queryString) {
      // These additional filterings only apply when a query string has been set
      return searchResult;
    }

    const {
      codeBody,
      codeUsage,
      namesManufacturer,
      namesModel,
      locations,
      vehicleIds
    } = additionalSearchParams;

    const vehiclesWithAdditionalParams = [
      ...searchResult.filter(vehicle => codeBody.includes(vehicle.codeBody)),
      ...searchResult.filter(vehicle => codeUsage.includes(vehicle.codeUsage)),
      ...searchResult.filter(vehicle =>
        namesManufacturer.includes(vehicle.manufacturer)
      ),
      ...searchResult.filter(vehicle => namesModel.includes(vehicle.model)),
      ...searchResult.filter(vehicle => vehicleIds.includes(vehicle.vehicleId)),
      ...searchResult.filter(vehicle =>
        locations.includes(vehicle.codeLocation)
      )
    ];

    // remove duplicate entries
    const result: BzitVehicleSearch[] = [];
    vehiclesWithAdditionalParams.forEach(vehicle => {
      if (!result.includes(vehicle)) {
        result.push(vehicle);
      }
    });

    return result;
  };

  public async componentDidMount() {
    const [formParams, countryId] = await Promise.all([
      this.ensureFormData(),
      this.queryCountryId(),
      this.fetchContentAsync(),
      this.getAllVehicles()
    ]);

    this.setState({
      formParams,
      resultsAreDirty: true,
      countryId,
      countUsage: this.getCountUsages(this.state.allVehicles),
    });

    // This is done seperately to avoid triggering a search when updating the initial parameters
    this.setState({
      searchIsReady: true,
    });
  }

  /**
   *
   * @param {SearchProviderProps} prevProps
   * @param {SearchState} prevState
   *
   * props to check:
   * - lang
   * state to check:
   * - countryId
   * - formParams
   */
  public async componentDidUpdate(
    prevProps: SearchProviderProps & WithLangProps,
    prevState: SearchState
  ) {
    if (prevProps.lang !== this.props.lang) {
      this.setState({
        locale: this.getLocale()
      });
    }
    if (
      prevState.countryId !== this.state.countryId ||
      prevState.locale !== this.state.locale
    ) {
      await this.putCountryId(this.state.countryId);
      await this.fetchContentAsync();
    }

    if (prevState.formParams.codeWheelpos !== this.state.formParams.codeWheelpos) {
      this.setState({countUsage: this.getCountUsages(this.state.allVehicles)});
    }

    if (!this.state.searchIsReady) {
      return;
    }

    if (prevState.formParams !== this.state.formParams) {
      await this.updateResults(this.state.formParams, this.state.additionalSearchParams);
    } else if (prevState.additionalSearchParams !== this.state.additionalSearchParams) {
      this.setState({
        searchResult: this.filterResultsWithAdditionalParams(this.state.apiSearchResult, this.state.additionalSearchParams),
      });
    }
  }

  public render() {
    return (
      <SearchContext.Provider value={this.state}>
        {this.props.children}
      </SearchContext.Provider>
    );
  }
}

export default withErrorBoundary(withTrackingApi(withLang(withTypo3(SearchProvider))), 'SearchProvider');
